import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Save, Trash2, GripVertical, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

export default function SuperAdminSchema() {
  const [networks, setNetworks] = useState([]);
  const [selected, setSelected] = useState("");
  const [schema, setSchema] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/networks").then((r) => {
      setNetworks(r.data);
      if (r.data[0]) setSelected(r.data[0].id);
    });
  }, []);

  useEffect(() => {
    if (!selected) return;
    api.get(`/superadmin/schema/${selected}`).then((r) => setSchema(r.data));
  }, [selected]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/superadmin/schema/${selected}`, {
        categories: schema.categories,
        fulfillment_methods: schema.fulfillment_methods,
        availability_statuses: schema.availability_statuses,
        product_custom_fields: schema.product_custom_fields,
        variant_custom_fields: schema.variant_custom_fields,
      });
      toast.success("Schema aggiornato — il prossimo export XLSX userà queste tendine");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  if (!schema) return <AdminLayout><div className="p-8">Caricamento...</div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="p-6 lg:p-8" data-testid="sa-schema-page">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="overline text-brand mb-1">SuperAdmin · Configurazione</div>
            <h1 className="font-heading font-bold text-3xl flex items-center gap-2"><FileSpreadsheet className="w-7 h-7" />Schema Builder</h1>
            <p className="text-sm text-muted-foreground mt-1">Definisci i valori delle tendine che apparriranno nel CSV/XLSX scaricato. Cambia qui → si applicano al prossimo export.</p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={selected} onValueChange={setSelected}>
              <SelectTrigger className="h-10 w-60 rounded-sm" data-testid="schema-network"><SelectValue /></SelectTrigger>
              <SelectContent>{networks.map((n) => <SelectItem key={n.id} value={n.id}>{n.display_name || n.name}</SelectItem>)}</SelectContent>
            </Select>
            <button onClick={save} disabled={busy} data-testid="schema-save"
              className="h-10 px-4 rounded-sm bg-foreground text-white text-sm font-medium hover:bg-brand inline-flex items-center gap-2 disabled:opacity-50">
              <Save className="w-4 h-4" /> {busy ? "Salvataggio..." : "Salva schema"}
            </button>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-4">
          <SimpleList title="📂 Categorie prodotto" hint="Compaiono nella tendina 'Categoria' del CSV"
            values={schema.categories}
            onChange={(v) => setSchema({ ...schema, categories: v })}
            placeholder="es. divani-letto"
            testid="categories" />

          <SimpleList title="🚚 Modalità di evasione" hint="Tendina 'Modalità di evasione' del CSV"
            values={schema.fulfillment_methods}
            onChange={(v) => setSchema({ ...schema, fulfillment_methods: v })}
            placeholder="es. drop_shipping"
            testid="fulfillment" />

          <ObjectList title="📊 Stati di disponibilità" hint="Es. 'in pronta consegna', 'su prenotazione'"
            values={schema.availability_statuses}
            fields={[{ k: "key", label: "Chiave tecnica", placeholder: "ordinabile_breve" },
                     { k: "label", label: "Etichetta utente", placeholder: "Pronta consegna" },
                     { k: "color", label: "Colore (HEX)", placeholder: "#10b981" }]}
            onChange={(v) => setSchema({ ...schema, availability_statuses: v })}
            testid="statuses" />

          <ObjectList title="🏷️ Campi custom prodotto" hint="Diventano colonne aggiuntive nel CSV"
            values={schema.product_custom_fields}
            fields={[{ k: "key", label: "Chiave", placeholder: "origine_paese" },
                     { k: "label", label: "Etichetta", placeholder: "Origine paese" },
                     { k: "type", label: "Tipo", placeholder: "string|number|enum" }]}
            onChange={(v) => setSchema({ ...schema, product_custom_fields: v })}
            testid="prod-fields" />

          <ObjectList title="🎨 Campi custom variante" hint="Es. colore, materiale, dimensione"
            values={schema.variant_custom_fields}
            fields={[{ k: "key", label: "Chiave", placeholder: "colore" },
                     { k: "label", label: "Etichetta", placeholder: "Colore" },
                     { k: "type", label: "Tipo", placeholder: "string|number|enum" }]}
            onChange={(v) => setSchema({ ...schema, variant_custom_fields: v })}
            testid="var-fields" />
        </div>
      </div>
    </AdminLayout>
  );
}

const SimpleList = ({ title, hint, values, onChange, placeholder, testid }) => {
  const [newVal, setNewVal] = useState("");
  return (
    <div className="bg-white border border-border rounded-sm p-4" data-testid={`schema-${testid}`}>
      <h3 className="font-heading font-bold text-base mb-1">{title}</h3>
      <p className="text-xs text-muted-foreground mb-3">{hint}</p>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {values.map((v, i) => (
          <span key={i} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-sm bg-neutral-100 border border-border">
            {v}
            <button onClick={() => onChange(values.filter((_, idx) => idx !== i))} className="text-red-500 hover:text-red-700">×</button>
          </span>
        ))}
        {values.length === 0 && <span className="text-xs text-muted-foreground">Nessun valore</span>}
      </div>
      <div className="flex gap-2">
        <input value={newVal} onChange={(e) => setNewVal(e.target.value)} placeholder={placeholder}
          data-testid={`schema-${testid}-input`}
          onKeyDown={(e) => { if (e.key === "Enter" && newVal.trim()) { onChange([...values, newVal.trim()]); setNewVal(""); } }}
          className="flex-1 h-9 px-2 rounded-sm border border-border text-sm" />
        <button onClick={() => { if (newVal.trim()) { onChange([...values, newVal.trim()]); setNewVal(""); } }}
          data-testid={`schema-${testid}-add`}
          className="h-9 px-3 rounded-sm border border-border hover:bg-accent text-xs inline-flex items-center gap-1">
          <Plus className="w-3.5 h-3.5" /> Aggiungi
        </button>
      </div>
    </div>
  );
};

const ObjectList = ({ title, hint, values, fields, onChange, testid }) => {
  const addEmpty = () => onChange([...values, Object.fromEntries(fields.map((f) => [f.k, ""]))]);
  return (
    <div className="bg-white border border-border rounded-sm p-4" data-testid={`schema-${testid}`}>
      <h3 className="font-heading font-bold text-base mb-1">{title}</h3>
      <p className="text-xs text-muted-foreground mb-3">{hint}</p>
      <div className="space-y-2">
        {values.map((v, i) => (
          <div key={i} className="grid grid-cols-[1fr_1fr_1fr_30px] gap-2 items-center">
            {fields.map((f) => (
              <input key={f.k} value={v[f.k] || ""} onChange={(e) => { const c = [...values]; c[i] = { ...c[i], [f.k]: e.target.value }; onChange(c); }}
                placeholder={f.placeholder}
                className="h-9 px-2 rounded-sm border border-border text-xs" />
            ))}
            <button onClick={() => onChange(values.filter((_, idx) => idx !== i))} className="w-9 h-9 rounded-sm border border-border text-red-500 hover:bg-red-50">
              <Trash2 className="w-3.5 h-3.5 mx-auto" />
            </button>
          </div>
        ))}
      </div>
      <button onClick={addEmpty} data-testid={`schema-${testid}-add`}
        className="mt-3 h-9 px-3 rounded-sm border border-dashed border-border hover:bg-accent text-xs inline-flex items-center gap-1">
        <Plus className="w-3.5 h-3.5" /> Aggiungi
      </button>
    </div>
  );
};
